import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { Session } from "../features/sessions/models/Session";
import { Invitee } from "../features/sessions/models/Invitee";
import { InviteeType } from "../features/sessions/models/InviteeType";
import { PaymentActivityType } from "../features/sessions/models/PaymentActivityType";
import { BookingStatus } from "../features/sessions/models/BookingStatus";
import { LoadingStatus } from "../features/sessions/models/LoadingStatus";
import {
  mapFrequencyToRecurrenceType,
  mapRecurrenceTypeToFrequency,
} from "../features/sessions/utils/RecurrenceTypeMapper";
import {
  fetchSessionById,
  createSession,
  updateSession,
  addInviteeToSession,
  removeInviteeFromSession,
} from "../features/sessions/slice-extensions/sessions-slice-extension";
import {
  setCurrentSession,
  selectCurrentSession,
  selectSessionsStatus,
} from "../features/sessions/sessions-slice";
import {
  fetchMembers,
  fetchGroups,
} from "../features/members/slice-extensions/members-slice-extension";
import {
  selectMembers,
  selectGroups,
} from "../features/members/members-slice";
import { ROUTES } from "../routes";
import Breadcrumbs from "../components/Breadcrumbs";
import Input from "../components/input/Input";
import Textarea from "../components/textarea/Textarea";
import Button from "../components/button/Button";
import DatePicker from "../components/date-picker/DatePicker";
import TimeSelector from "../components/time-selector/TimeSelector";
import DurationSelector from "../components/duration-selector/DurationSelector";
import FrequencySelector from "../components/frequency-selector/FrequencySelector";
import LeftMenu from "../components/LeftMenu";
import ConfirmationModal from "../components/ConfirmationModal";
import {
  validateSession,
  FormErrors,
  showValidationErrorNotification,
} from "../utils/validation";
import {
  showSuccessNotification,
  showErrorNotification,
  SESSION_NOTIFICATIONS,
} from "../utils/notifications";
import { extractTypedApiErrorMessage } from "../api/utils/errorHandling";

/** Hash a string to a positive integer */
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash |= 0;
  }
  return Math.abs(hash);
};

const MEMBER_COLORS = [
  "#367D7D", "#2E6B6B", "#3D8F8F", "#2A7A5E",
  "#4A8C6F", "#3B7A9E", "#2D6E8A", "#458B74",
];
const GROUP_COLORS = [
  "#6317CF", "#462673", "#6C0DF2", "#8833FF",
  "#9966FF", "#993300", "#C2570A", "#0F8A80",
];

const getMemberColor = (firstName: string, lastName: string): string =>
  MEMBER_COLORS[hashString(firstName + lastName) % MEMBER_COLORS.length];

const getGroupColor = (name: string): string =>
  GROUP_COLORS[hashString(name) % GROUP_COLORS.length];

const getInitials = (firstName: string, lastName: string): string =>
  `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

const getGroupAbbreviation = (name: string): string => {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  return name.trim().substring(0, 2).toUpperCase();
};

const CreateEditSession = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  const currentSession = useAppSelector(selectCurrentSession);
  const sessionLoadingStatus = useAppSelector(selectSessionsStatus);
  const isFetching = sessionLoadingStatus === LoadingStatus.Loading;

  const allMembers = useAppSelector(selectMembers);
  const allGroups = useAppSelector(selectGroups);

  const [errors, setErrors] = useState<FormErrors>({});
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [originalSession, setOriginalSession] = useState<Session | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [participantSearchText, setParticipantSearchText] = useState("");
  const [showParticipantDropdown, setShowParticipantDropdown] = useState(false);

  // Determine if we're in edit mode based on URL parameter
  const isEditMode = Boolean(id);

  // Initialize session for create mode or load for edit mode
  useEffect(() => {
    const initializeSession = async () => {
      if (isEditMode && id) {
        try {
          // Fetch session using Redux action
          const result = await dispatch(fetchSessionById(id));
          if (fetchSessionById.fulfilled.match(result)) {
            const session = result.payload as Session;
            // Store original session for change detection (deep copy as plain strings)
            setOriginalSession({ ...session });
          } else {
            showErrorNotification(SESSION_NOTIFICATIONS.LOAD_ERROR);
            navigate(ROUTES.SESSIONS);
            return;
          }
        } catch (error) {
          showErrorNotification(SESSION_NOTIFICATIONS.LOAD_ERROR);
          navigate(ROUTES.SESSIONS);
        }
      } else {
        // Create mode: initialize with default session (string dates per model)
        const now = new Date();
        const nowIso = new Date(now.setHours(0, 0, 0, 0)).toISOString();
        const defaultSession: Session = {
          id: "",
          paymentActivityType: PaymentActivityType.Regular,
          title: "",
          description: undefined,
          dateStart: nowIso,
          timeLocal: "",
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          duration: 0,
          isAllDay: false,
          status: BookingStatus.Active,
          recurrenceType: mapFrequencyToRecurrenceType("none"),
          invitees: [],
          dateCreated: nowIso,
          dateModified: nowIso,
        };
        dispatch(setCurrentSession(defaultSession));
        // Store original session for change detection
        setOriginalSession({ ...defaultSession });
      }
    };

    initializeSession();
  }, [isEditMode, id, dispatch, navigate]);

  // Clear errors when session changes (but not during submission to prevent scroll issues)
  useEffect(() => {
    if (currentSession && !isSubmitting) {
      setErrors({});
    }
  }, [currentSession, isSubmitting]);

  // Fetch members and groups for participant picker
  useEffect(() => {
    dispatch(fetchMembers());
    dispatch(fetchGroups());
  }, [dispatch]);

  // Resolved participant data for display
  const resolvedParticipants = useMemo(() => {
    if (!currentSession) return [];
    const memberMap = new Map(allMembers.map((m) => [m.id, m]));
    const groupMap = new Map(allGroups.map((g) => [g.id, g]));
    return currentSession.invitees.map((inv) => ({
      invitee: inv,
      member: inv.type === InviteeType.Individual ? memberMap.get(inv.invitedUserId) : undefined,
      group: inv.type === InviteeType.Group ? groupMap.get(inv.invitedUserId) : undefined,
    }));
  }, [currentSession?.invitees, allMembers, allGroups]);

  // Filtered members + groups for the search dropdown
  const filteredParticipants = useMemo(() => {
    const selectedIds = new Set(currentSession?.invitees.map((i) => i.invitedUserId) || []);
    const search = participantSearchText.trim().toLowerCase();

    const matchingGroups = allGroups
      .filter((g) => !selectedIds.has(g.id))
      .filter((g) => !search || g.name.toLowerCase().includes(search));

    const matchingMembers = allMembers
      .filter((m) => !selectedIds.has(m.id))
      .filter((m) => {
        if (!search) return true;
        const fullName = `${m.firstName} ${m.lastName}`.toLowerCase();
        return fullName.includes(search) || (m.email?.toLowerCase().includes(search) ?? false);
      });

    return { groups: matchingGroups, members: matchingMembers };
  }, [allGroups, allMembers, currentSession?.invitees, participantSearchText]);

  const getGroupMemberCount = (groupId: string): number =>
    allMembers.filter((m) => m.groups.some((g) => g.id === groupId)).length;

  const handleAddParticipant = async (entityId: string, type: InviteeType) => {
    if (isEditMode && currentSession?.id) {
      // Edit mode: call API directly
      const typeNum = type === InviteeType.Group ? 0 : 1;
      const result = await dispatch(
        addInviteeToSession({ sessionId: currentSession.id, invitedUserId: entityId, type: typeNum })
      );
      if (addInviteeToSession.fulfilled.match(result)) {
        // The thunk returns the updated session — update Redux
        dispatch(setCurrentSession(result.payload));
      }
    } else if (currentSession) {
      // Create mode: add to local invitees array
      const newInvitee: Invitee = {
        id: "",
        invitedUserId: entityId,
        bookingId: "",
        type,
        dateCreated: "",
        dateModified: "",
      };
      dispatch(setCurrentSession({
        ...currentSession,
        invitees: [...currentSession.invitees, newInvitee],
      }));
    }
    setParticipantSearchText("");
    setShowParticipantDropdown(false);
  };

  const handleRemoveParticipant = async (invitee: Invitee) => {
    if (isEditMode && currentSession?.id && invitee.id) {
      // Edit mode: call API directly
      const result = await dispatch(
        removeInviteeFromSession({ sessionId: currentSession.id, inviteeId: invitee.id })
      );
      if (removeInviteeFromSession.fulfilled.match(result)) {
        dispatch(setCurrentSession(result.payload));
      }
    } else if (currentSession) {
      // Create mode: remove from local invitees array
      dispatch(setCurrentSession({
        ...currentSession,
        invitees: currentSession.invitees.filter((i) => i.invitedUserId !== invitee.invitedUserId),
      }));
    }
  };

  // Helper function to check if there are unsaved changes
  const hasUnsavedChanges = (): boolean => {
    if (!currentSession || !originalSession) {
      return false;
    }

    // Compare relevant fields that user can modify
    const titleChanged =
      (currentSession.title || "") !== (originalSession.title || "");
    const descriptionChanged =
      (currentSession.description || "") !==
      (originalSession.description || "");

    // Handle date comparison more safely
    let dateChanged = false;
    try {
      const currentDate = new Date(currentSession.dateStart);
      const originalDate = new Date(originalSession.dateStart);
      if (!isNaN(currentDate.getTime()) && !isNaN(originalDate.getTime())) {
        dateChanged = currentDate.getTime() !== originalDate.getTime();
      }
    } catch (error) {
      dateChanged = false;
    }

    const timeChanged =
      (currentSession.timeLocal || "") !== (originalSession.timeLocal || "");
    const durationChanged =
      (currentSession.duration || 0) !== (originalSession.duration || 0);
    const recurrenceChanged =
      currentSession.recurrenceType !== originalSession.recurrenceType;

    const currentInviteeIds = new Set(currentSession.invitees.map((i) => i.invitedUserId));
    const originalInviteeIds = new Set(originalSession.invitees.map((i) => i.invitedUserId));
    const inviteesChanged =
      currentInviteeIds.size !== originalInviteeIds.size ||
      [...currentInviteeIds].some((id) => !originalInviteeIds.has(id));

    return (
      titleChanged ||
      descriptionChanged ||
      dateChanged ||
      timeChanged ||
      durationChanged ||
      recurrenceChanged ||
      inviteesChanged
    );
  };

  // Helper function to navigate back to previous page
  const navigateToPreviousPage = () => {
    // Check if we have a referrer in location state
    const referrer = location.state?.from;
    if (referrer) {
      navigate(referrer);
      return;
    }

    // Try to go back in browser history if available
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    // Fallback: If in edit mode, go to session details; otherwise go to sessions list
    const fallbackDestination =
      isEditMode && currentSession?.id
        ? ROUTES.SESSION_DETAILS.replace(":id", currentSession.id)
        : ROUTES.SESSIONS;

    navigate(fallbackDestination);
  };

  const handleInputChange =
    (field: "title" | "description") =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = e.target.value;

      if (currentSession) {
        const updatedSession: Session = {
          ...currentSession,
          [field]: field === "description" ? newValue || undefined : newValue,
        };
        dispatch(setCurrentSession(updatedSession));
      }

      // Clear error when user starts typing
      const errorField = field === "title" ? "eventName" : "eventNotes";
      if (errors[errorField]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[errorField];
          return newErrors;
        });
      }
    };

  const handleDateSelect = (date: Date) => {
    if (currentSession) {
      const normalizedDate = new Date(date);
      normalizedDate.setHours(0, 0, 0, 0);
      const updatedSession: Session = {
        ...currentSession,
        dateStart: normalizedDate.toISOString(),
      };
      dispatch(setCurrentSession(updatedSession));
    }

    // Clear date error when user selects a date
    if (errors.eventDate) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.eventDate;
        return newErrors;
      });
    }
  };

  const handleTimeSelect = (time: string) => {
    if (currentSession) {
      const updatedSession: Session = {
        ...currentSession,
        timeLocal: time,
      };
      dispatch(setCurrentSession(updatedSession));
    }

    // Clear time error when user selects a time
    if (errors.startTime) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.startTime;
        return newErrors;
      });
    }
  };

  const handleDurationSelect = (duration: string) => {
    if (currentSession) {
      const durationNum = parseInt(duration, 10);
      const updatedSession: Session = {
        ...currentSession,
        duration: isNaN(durationNum) ? 0 : durationNum,
      };
      dispatch(setCurrentSession(updatedSession));
    }

    // Clear duration error when user selects a duration
    if (errors.duration) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.duration;
        return newErrors;
      });
    }
  };

  const handleFrequencySelect = (frequency: string) => {
    if (currentSession) {
      const updatedSession: Session = {
        ...currentSession,
        recurrenceType: mapFrequencyToRecurrenceType(frequency),
      };
      dispatch(setCurrentSession(updatedSession));
    }

    // Clear frequency error when user selects a frequency
    if (errors.frequency) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.frequency;
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (isSubmitting) return;

    // Store current scroll position before submission
    const scrollPosition = window.pageYOffset;
    setIsSubmitting(true);

    // Clear previous errors
    setErrors({});

    // Validate required fields using utility function
    const newErrors = validateSession(currentSession, isEditMode);

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      showValidationErrorNotification(newErrors);
      scrollToFirstError(newErrors);
      return;
    }

    if (!currentSession) {
      const errorMessage = isEditMode
        ? SESSION_NOTIFICATIONS.UPDATE_ERROR
        : SESSION_NOTIFICATIONS.CREATE_ERROR;
      setIsSubmitting(false);
      showErrorNotification(errorMessage);
      return;
    }

    try {
      let result;

      if (isEditMode && currentSession.id) {
        // Update existing session using currentSession from Redux
        result = await dispatch(
          updateSession({
            id: currentSession.id,
            session: currentSession,
          })
        );
      } else {
        // Create new session using currentSession from Redux
        result = await dispatch(createSession(currentSession));
      }

      if (
        createSession.fulfilled.match(result) ||
        updateSession.fulfilled.match(result)
      ) {
        // Restore scroll position to prevent jumping to top
        window.scrollTo(0, scrollPosition);

        const successMessage = isEditMode
          ? SESSION_NOTIFICATIONS.UPDATED_SUCCESS
          : SESSION_NOTIFICATIONS.CREATED_SUCCESS;
        showSuccessNotification(successMessage, 3000);
        setTimeout(() => {
          setIsSubmitting(false);

          let navigateRoute: string;

          if (isEditMode) {
            // For edit mode: Check if we have a referrer in location state for proper redirect
            const referrer = location.state?.from;
            if (referrer) {
              // Navigate back to the page that initiated the edit
              navigateRoute = referrer;
            } else {
              // Fallback: go to session details
              navigateRoute = ROUTES.SESSION_DETAILS.replace(
                ":id",
                currentSession?.id || ""
              );
            }
          } else {
            // For create mode: Always navigate to sessions list
            navigateRoute = ROUTES.SESSIONS;
          }

          navigate(navigateRoute, {
            replace: false,
            preventScrollReset: true,
          });
        }, 1000);
      } else {
        // Handle API validation errors or other failures
        const defaultErrorMessage = isEditMode
          ? SESSION_NOTIFICATIONS.UPDATE_ERROR
          : SESSION_NOTIFICATIONS.CREATE_ERROR;
        const errorMessage = extractTypedApiErrorMessage(
          result.payload as any,
          defaultErrorMessage
        );
        setIsSubmitting(false);
        showErrorNotification(errorMessage);
      }
    } catch (error) {
      console.error("Error saving session:", error);
      const errorMessage = isEditMode
        ? SESSION_NOTIFICATIONS.UPDATE_ERROR
        : SESSION_NOTIFICATIONS.CREATE_ERROR;
      setIsSubmitting(false);
      showErrorNotification(errorMessage);
    }
  };

  const handleCancel = () => {
    // In edit mode, always show confirmation to be safe
    // In create mode, check for changes
    if (isEditMode || hasUnsavedChanges()) {
      setShowCancelConfirmation(true);
    } else {
      // No changes, navigate directly
      setErrors({});
      navigateToPreviousPage();
    }
  };

  const handleCancelConfirm = () => {
    setShowCancelConfirmation(false);
    setErrors({});
    navigateToPreviousPage();
  };

  const handleCancelCancel = () => {
    setShowCancelConfirmation(false);
  };

  const scrollToFirstError = (errors: FormErrors): void => {
    setTimeout(() => {
      const firstErrorField = Object.keys(errors)[0];
      let targetElement: Element | null = null;
      let elementId = "";

      switch (firstErrorField) {
        case "eventName":
          elementId = "event_name";
          break;
        case "eventDate":
          // For date picker, scroll to the date picker container
          targetElement =
            document.querySelector('[data-testid="date-picker"]') ||
            document.querySelector(".date-picker") ||
            document.querySelector('input[type="date"]');
          break;
        case "startTime":
          // For time selector, scroll to time selector container with multiple fallbacks
          targetElement =
            document.querySelector('[data-testid="time-selector"]') ||
            document.querySelector(".start-time-tabs") ||
            document.querySelector(".time-selector") ||
            document.querySelector('input[type="time"]');
          break;
        case "duration":
          // For duration selector, scroll to duration container
          targetElement =
            document.querySelector('[data-testid="duration-selector"]') ||
            document.querySelector(".duration-selector");
          break;
        case "frequency":
          // For frequency selector, scroll to frequency container
          targetElement =
            document.querySelector('[data-testid="frequency-selector"]') ||
            document.querySelector(".frequency-selector");
          break;
        case "eventNotes":
          elementId = "session_notes";
          break;
      }

      // Try to find element by ID if not found by selector
      if (!targetElement && elementId) {
        targetElement = document.getElementById(elementId);
      }

      if (targetElement) {
        // Calculate smooth scroll position with proper offset for better UX
        const elementRect = targetElement.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;

        // Instead of centering, position the element near the top with reasonable padding
        // This prevents over-scrolling and ensures the error message is visible
        const headerOffset = 100; // Account for any fixed headers
        const targetScrollPosition = Math.max(
          0,
          absoluteElementTop - headerOffset
        );

        // Use smooth scrolling with gentle positioning
        window.scrollTo({
          top: targetScrollPosition,
          behavior: "smooth",
        });

        // Focus the element if it's focusable (after scroll completes)
        setTimeout(() => {
          if (
            targetElement instanceof HTMLElement &&
            (targetElement.tagName === "INPUT" ||
              targetElement.tagName === "TEXTAREA" ||
              targetElement.tagName === "SELECT" ||
              targetElement.hasAttribute("tabindex"))
          ) {
            targetElement.focus();
          }
        }, 500);
      }
    }, 150);
  };

  return (
    <section>
      <div className="flex h-full gap-4">
        {/* Left Side */}
        <LeftMenu />
        {/* Right Side */}
        <div className="w-full p-4 pb-10 lg:px-10">
          <div className="lg:max-w-[694px]">
            {/* Breadcrumbs */}
            <Breadcrumbs />

            {/* Content */}
            <div className="space-y-4 py-6 lg:space-y-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <h1>{isEditMode ? t("sessions.editSession") : t("sessions.createSession")}</h1>
                <div className="flex flex-row items-center gap-3 w-full lg:w-auto">
                  <Link
                    to={ROUTES.SESSIONS}
                    className="btn btn-secondary flex-1 lg:flex-none lg:w-auto flex items-center justify-center"
                  >
                    <svg
                      width={16}
                      height={16}
                      viewBox="6 6 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"
                        fill="currentColor"
                      />
                    </svg>
                    <span>{t("common.backToList")}</span>
                  </Link>
                </div>
              </div>

              {/* Only show form when not fetching or when in create mode */}
              {(!isFetching || !isEditMode) && (
                <form
                  onSubmit={handleSubmit}
                  className="space-y-6 lg:space-y-4"
                >
                  {/* Schedule Details */}
                  <div className="space-y-6 lg:space-y-5">
                    <h3>{t("sessions.scheduleDetails")}</h3>

                    <Input
                      label={t("sessions.titleLabel")}
                      type="text"
                      name="event_name"
                      id="event_name"
                      placeholder={t("sessions.titlePlaceholder")}
                      value={currentSession?.title || ""}
                      onChange={handleInputChange("title")}
                      error={errors.eventName}
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Session Date */}
                  <DatePicker
                    selectedDate={
                      currentSession?.dateStart
                        ? new Date(currentSession.dateStart)
                        : undefined
                    }
                    onDateSelect={handleDateSelect}
                    disabled={isSubmitting}
                  />

                  {/* Error message for date selection */}
                  {errors.eventDate && (
                    <p className="field-error mt-1">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 0C12.4183 0 16 3.58172 16 8C16 10.1217 15.1575 12.1569 13.6572 13.6572C12.1569 15.1575 10.1217 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0ZM8.19043 11.0195C7.86419 10.9542 7.52644 11.0559 7.29004 11.29C7.10286 11.4788 6.99846 11.7342 7 12C6.99846 12.2658 7.10286 12.5212 7.29004 12.71C7.38724 12.7983 7.49893 12.8693 7.62012 12.9199C7.86212 13.0269 8.13788 13.0269 8.37988 12.9199C8.50106 12.8693 8.61276 12.7983 8.70996 12.71C8.89715 12.5212 9.00154 12.2658 9 12C8.99632 11.7352 8.89269 11.4817 8.70996 11.29C8.66244 11.247 8.61195 11.2068 8.55957 11.1699C8.50392 11.1322 8.44348 11.1019 8.37988 11.0801C8.32046 11.0497 8.25652 11.0288 8.19043 11.0195ZM8 3C7.44772 3 7 3.33579 7 3.75V8.25C7 8.66421 7.44772 9 8 9C8.55229 9 9 8.66421 9 8.25V3.75C9 3.33579 8.55229 3 8 3Z"
                          fill="currentColor"
                        />
                      </svg>
                      {errors.eventDate}
                    </p>
                  )}

                  {/* Start Time */}
                  <TimeSelector
                    selectedTime={
                      currentSession?.timeLocal
                        ? currentSession.timeLocal.includes(":")
                          ? currentSession.timeLocal
                              .split(":")
                              .slice(0, 2)
                              .join(":")
                          : currentSession.timeLocal
                        : ""
                    }
                    onTimeSelect={handleTimeSelect}
                    error={errors.startTime}
                    disabled={isSubmitting}
                    selectedDate={
                      currentSession?.dateStart
                        ? new Date(currentSession.dateStart)
                        : undefined
                    }
                  />

                  {/* Duration */}
                  <DurationSelector
                    selectedDuration={
                      currentSession?.duration
                        ? currentSession.duration.toString()
                        : ""
                    }
                    onDurationSelect={handleDurationSelect}
                    error={errors.duration}
                    disabled={isSubmitting}
                  />

                  {/* Frequency */}
                  <FrequencySelector
                    selectedFrequency={
                      currentSession
                        ? mapRecurrenceTypeToFrequency(
                            currentSession.recurrenceType
                          )
                        : "none"
                    }
                    onFrequencySelect={handleFrequencySelect}
                    isDisabled={isSubmitting}
                  />

                  {/* Error message for frequency selection */}
                  {errors.frequency && (
                    <p className="field-error mt-1">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 0C12.4183 0 16 3.58172 16 8C16 10.1217 15.1575 12.1569 13.6572 13.6572C12.1569 15.1575 10.1217 16 8 16C3.58172 16 0 12.4183 0 8C0 3.58172 3.58172 0 8 0ZM8.19043 11.0195C7.86419 10.9542 7.52644 11.0559 7.29004 11.29C7.10286 11.4788 6.99846 11.7342 7 12C6.99846 12.2658 7.10286 12.5212 7.29004 12.71C7.38724 12.7983 7.49893 12.8693 7.62012 12.9199C7.86212 13.0269 8.13788 13.0269 8.37988 12.9199C8.50106 12.8693 8.61276 12.7983 8.70996 12.71C8.89715 12.5212 9.00154 12.2658 9 12C8.99632 11.7352 8.89269 11.4817 8.70996 11.29C8.66244 11.247 8.61195 11.2068 8.55957 11.1699C8.50392 11.1322 8.44348 11.1019 8.37988 11.0801C8.32046 11.0497 8.25652 11.0288 8.19043 11.0195ZM8 3C7.44772 3 7 3.33579 7 3.75V8.25C7 8.66421 7.44772 9 8 9C8.55229 9 9 8.66421 9 8.25V3.75C9 3.33579 8.55229 3 8 3Z"
                          fill="currentColor"
                        />
                      </svg>
                      {errors.frequency}
                    </p>
                  )}

                  {/* Divider */}
                  <hr className="border-grey-70" />

                  {/* Participants */}
                  <div className="space-y-6 lg:space-y-5">
                    <div className="space-y-2">
                      <h3>
                        {t("sessions.participantsCount", {
                          count: currentSession?.invitees.length || 0,
                        })}
                      </h3>
                      {(!currentSession?.invitees.length) && (
                        <p className="text-grey-40 dark:text-grey-80">
                          {t("sessions.noParticipantsYet")}
                        </p>
                      )}
                    </div>

                    {/* Selected participants list */}
                    {resolvedParticipants.length > 0 && (
                      <div className="overflow-hidden">
                        {resolvedParticipants.map((p) => {
                          const isGroup = p.invitee.type === InviteeType.Group;
                          const name = isGroup
                            ? p.group?.name || "Unknown Group"
                            : p.member
                              ? `${p.member.firstName} ${p.member.lastName}`
                              : "Unknown Member";
                          const email = !isGroup ? p.member?.email : undefined;
                          const avatarColor = isGroup
                            ? getGroupColor(p.group?.name || "")
                            : getMemberColor(p.member?.firstName || "", p.member?.lastName || "");
                          const avatarText = isGroup
                            ? getGroupAbbreviation(p.group?.name || "")
                            : getInitials(p.member?.firstName || "", p.member?.lastName || "");
                          const memberCount = isGroup ? getGroupMemberCount(p.invitee.invitedUserId) : 0;

                          return (
                            <div
                              key={p.invitee.invitedUserId}
                              className="flex w-full items-center justify-between gap-3"
                            >
                              <div className="flex shrink items-center gap-3 py-2">
                                <div className="flex items-center gap-2">
                                  <div
                                    className="relative flex size-8 items-center justify-center rounded-full"
                                    style={{ backgroundColor: avatarColor }}
                                  >
                                    <span className="text-xs font-bold text-white">
                                      {avatarText}
                                    </span>
                                    {isGroup && (
                                      <div className="notification-bottom border-white dark:border-primary-10">
                                        {memberCount}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div>
                                  <p className="line-clamp-1">{name}</p>
                                  {email && (
                                    <p className="text-grey-50 dark:text-grey-70 line-clamp-1 max-w-fit text-sm break-all">
                                      {email}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <button
                                type="button"
                                className="btn btn-text dark:hover:text-primary-70 dark:text-white"
                                onClick={() => handleRemoveParticipant(p.invitee)}
                                disabled={isSubmitting}
                              >
                                <span>{t("sessions.remove")}</span>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Participant search */}
                    <div className="relative">
                      <label className="block text-sm font-medium text-grey-30 dark:text-white mb-1">
                        {t("sessions.addParticipants")}
                      </label>
                      <input
                        type="text"
                        className="input"
                        placeholder={t("sessions.searchParticipantsPlaceholder")}
                        value={participantSearchText}
                        onChange={(e) => {
                          setParticipantSearchText(e.target.value);
                          setShowParticipantDropdown(true);
                        }}
                        onFocus={() => setShowParticipantDropdown(true)}
                        disabled={isSubmitting}
                      />

                      {/* Dropdown */}
                      {showParticipantDropdown &&
                        (filteredParticipants.groups.length > 0 || filteredParticipants.members.length > 0) && (
                        <div className="absolute z-10 w-full mt-1 max-h-60 overflow-y-auto rounded-lg border border-grey-70 dark:border-primary-20 bg-white dark:bg-primary-20 shadow-lg">
                          {/* Groups */}
                          {filteredParticipants.groups.map((group) => {
                            const memberCount = getGroupMemberCount(group.id);
                            return (
                              <button
                                key={`group-${group.id}`}
                                type="button"
                                className="flex items-center gap-3 w-full px-4 py-2 text-left hover:bg-grey-95 dark:hover:bg-primary-30 text-grey-10 dark:text-grey-90 transition-colors"
                                onClick={() => handleAddParticipant(group.id, InviteeType.Group)}
                              >
                                <div
                                  className="relative flex size-8 items-center justify-center rounded-full shrink-0"
                                  style={{ backgroundColor: getGroupColor(group.name) }}
                                >
                                  <span className="text-xs font-bold text-white">
                                    {getGroupAbbreviation(group.name)}
                                  </span>
                                  <div className="notification-bottom">
                                    {memberCount}
                                  </div>
                                </div>
                                <p className="line-clamp-1 text-sm">{group.name}</p>
                              </button>
                            );
                          })}
                          {/* Members */}
                          {filteredParticipants.members.map((member) => (
                            <button
                              key={`member-${member.id}`}
                              type="button"
                              className="flex items-center gap-3 w-full px-4 py-2 text-left hover:bg-grey-95 dark:hover:bg-primary-30 text-grey-10 dark:text-grey-90 transition-colors"
                              onClick={() => handleAddParticipant(member.id, InviteeType.Individual)}
                            >
                              <div
                                className="flex size-8 items-center justify-center rounded-full shrink-0"
                                style={{ backgroundColor: getMemberColor(member.firstName, member.lastName) }}
                              >
                                <span className="text-xs font-bold text-white">
                                  {getInitials(member.firstName, member.lastName)}
                                </span>
                              </div>
                              <div className="min-w-0">
                                <p className="line-clamp-1 text-sm">
                                  {member.firstName} {member.lastName}
                                </p>
                                {member.email && (
                                  <p className="text-grey-50 dark:text-grey-70 line-clamp-1 text-xs break-all">
                                    {member.email}
                                  </p>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Divider */}
                  <hr className="border-grey-70" />

                  <div className="space-y-6 lg:space-y-5">
                    <h3>{t("sessions.sessionNotes")}</h3>

                    <Textarea
                      label={t("sessions.notesLabel")}
                      name="session_notes"
                      id="session_notes"
                      placeholder={t("sessions.notesPlaceholder")}
                      value={currentSession?.description || ""}
                      onChange={handleInputChange("description")}
                      error={errors.eventNotes}
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="flex items-start gap-3">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? isEditMode
                          ? t("common.saving")
                          : t("common.creating")
                        : isEditMode
                        ? t("common.saveChanges")
                        : t("sessions.createSession")}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleCancel}
                      disabled={isSubmitting}
                    >
                      {t("common.cancel")}
                    </Button>
                  </div>
                </form>
              )}
              {isFetching && isEditMode && (
                <div
                  className="flex items-center gap-2 text-gray-700 dark:text-gray-300"
                  role="status"
                  aria-live="polite"
                >
                  <div
                    className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent"
                    aria-hidden="true"
                  ></div>
                  <span>{t("sessions.sessionLoading")}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      <ConfirmationModal
        isOpen={showCancelConfirmation}
        title={t("sessions.discardChangesTitle")}
        message={
          isEditMode
            ? t("sessions.discardChangesEdit")
            : t("sessions.discardChangesCreate")
        }
        confirmText={t("common.discard")}
        cancelText={t("common.keepEditing")}
        onConfirm={handleCancelConfirm}
        onCancel={handleCancelCancel}
        variant="danger"
      />
    </section>
  );
};

export default CreateEditSession;
